import { useState, useEffect } from 'react';
import { createResourceLocatorString } from '../../../../util/routes';
import routeConfiguration from '../../../../routeConfiguration';

/**
 * Custom hook to manage post votes
 * @param {Object} post - The post object
 * @param {Object} currentUser - The current user
 * @param {Function} onAddPostCount - Function to update vote count on the server
 * @param {Object} location - Current location object
 * @param {Object} history - History object for navigation
 * @returns {Object} Vote state and handlers
 */
export const usePostVotes = (post, currentUser, onAddPostCount, location, history) => {
  const metadata = post?.attributes?.metadata;

  // Initialize local state for vote management
  const [voteCount, setVoteCount] = useState(metadata?.voteCount || 0);
  const [userVote, setUserVote] = useState(null);

  // Find initial user reaction
  const getCurrentUserVote = () => {
    if (!post?.attributes?.metadata?.reactions) {
      return null;
    }

    const userReaction = post.attributes.metadata.reactions.find(
      r => r.userId === currentUser?.id?.uuid
    );
    return userReaction?.type || null;
  };

  // Set initial user vote when component mounts or post changes
  useEffect(() => {
    const initialVote = getCurrentUserVote();
    setUserVote(initialVote);
  }, [post.id.uuid]);

  // Update vote count from props if it changes (e.g. server update)
  useEffect(() => {
    setVoteCount(metadata?.voteCount || 0);
  }, [metadata?.voteCount]);

  // Handle vote action
  const handleVote = action => {
    if (!currentUser?.id?.uuid) {
      const state = {
        from: `${location.pathname}${location.search}${location.hash}`,
      };
      return history.push(createResourceLocatorString('LoginPage', routeConfiguration()), state);
    }

    let newVoteCount = voteCount;
    let newUserVote = null;

    // Case 1: No previous vote, adding new one
    if (!userVote) {
      if (action === 'like') {
        newVoteCount = voteCount + 1;
        newUserVote = 'like';
      } else {
        newVoteCount = voteCount - 1;
        newUserVote = 'dislike';
      }
    }
    // Case 2: Clicking the same button again (toggling off)
    else if (userVote === action) {
      if (action === 'like') {
        newVoteCount = voteCount - 1;
      } else {
        newVoteCount = voteCount + 1;
      }
      newUserVote = null;
    }
    // Case 3: SWITCHING from one vote type to another
    else {
      if (action === 'like') {
        newVoteCount = voteCount + 2; // remove dislike (-1) and add like (+1)
        newUserVote = 'like';
      } else {
        newVoteCount = voteCount - 2; // remove like (+1) and add dislike (-1)
        newUserVote = 'dislike';
      }
    }

    // Update local state with new values
    setVoteCount(newVoteCount);
    setUserVote(newUserVote);

    // Call API to update in database
    onAddPostCount({
      listingId: post.id.uuid,
      action,
    });
  };

  return {
    voteCount,
    userVote,
    handleVote,
  };
};

/**
 * Custom hook to determine post content type and extract relevant data
 * @param {Object} post - The post object
 * @returns {Object} Post content information
 */
export const usePostContent = post => {
  const publicData = post?.attributes?.publicData;

  const isTextPost = publicData?.contentType === 'text';
  const isImagePost = publicData?.contentType === 'image';
  const isLinkPost = publicData?.contentType === 'link';

  const images = post.images || [];
  const linkUrl = publicData?.linkUrl;

  // Use descriptionHtml so paragraph/line breaks become spaces in preview
  const descriptionHtml = publicData?.descriptionHtml;
  const description = descriptionHtml
    ? descriptionHtml
        .replace(/<(br|\/p|\/li|\/h[1-6]|\/div|\/blockquote)[^>]*>/gi, ' ')
        .replace(/<[^>]+>/g, '')
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/\s+/g, ' ')
        .trim()
    : post?.attributes?.description;

  return {
    isTextPost,
    isImagePost,
    isLinkPost,
    images,
    linkUrl,
    description,
    publicData,
  };
};

/**
 * Custom hook to extract and format post metadata
 * @param {Object} post - The post object
 * @param {Array} categories - Available categories
 * @returns {Object} Formatted post metadata
 */
export const usePostMetadata = (post, categories) => {
  const { createdAt, publicData } = post?.attributes || {};
  const author = post.author;

  const authorDisplayName =
    author?.attributes?.profile?.publicData?.name?.firstName ||
    author?.attributes?.profile?.displayName;

  const commentsLength = publicData?.comments?.length || 0;
  const categoryKey = publicData?.category;
  const categoryLabel = categories.find(c => c.key === categoryKey)?.label;

  return {
    author,
    authorDisplayName,
    createdAt,
    commentsLength,
    categoryKey,
    categoryLabel,
  };
};
